import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Sparkles, Clock, CheckCircle } from 'lucide-react';
import {
  generateEliteSummary,
  extractEliteKeyPoints,
  getCurrentMonthYear,
  formatArticleDate
} from '../services/eliteSummaryGenerator';

/**
 * AISnippetBlock - 2026 AIO-Optimized Summary Block
 * 
 * This component creates a visually distinct "AI Summary" block that:
 * 1. Is easily extractable by Google's AI Overview
 * 2. Contains the Quick Answer (2-3 sentences) for instant answers
 * 3. Includes freshness signals (Updated 2026)
 * 4. Provides key points in bullet format
 * 
 * Place at the TOP of every article and landing page
 */
export const AISnippetBlock = ({
  quickAnswer,
  keyPoints = [],
  lastUpdated,
  variant = 'default', // 'default', 'compact', 'featured'
  content
}) => {
  if (!quickAnswer) return null;

  // Clean summary and use dynamic date - always use current month, ignore static data
  const cleanSummary = useMemo(() => generateEliteSummary(quickAnswer), [quickAnswer]);
  const dynamicDate = useMemo(() => getCurrentMonthYear(), []);
  const finalKeyPoints = useMemo(() => {
    return keyPoints.length > 0 ? keyPoints : (content ? extractEliteKeyPoints(content) : []);
  }, [keyPoints, content]);

  const variants = {
    default: 'bg-gradient-to-r from-primary/10 via-primary/5 to-transparent',
    compact: 'bg-card/50',
    featured: 'bg-gradient-to-r from-primary/20 via-primary/10 to-primary/5'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`${variants[variant]} border border-primary/30 rounded-xl p-6 mb-8`}
      // Semantic HTML for AI extraction
      itemScope
      itemType="https://schema.org/Answer"
    >
      {/* Header with search icon - SEO optimized Quick Answer */}
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-primary/20 rounded-lg">
          <Search className="w-5 h-5 text-primary" />
        </div>
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-lg">🔍 Quick Answer</h3>
          <span className="text-xs text-muted-foreground bg-white/5 px-2 py-0.5 rounded-full">2026</span>
        </div>
      </div>

      {/* Quick Answer - Primary extraction target */}
      <div
        className="text-foreground leading-relaxed mb-4"
        itemProp="text"
      >
        <p className="text-base">{cleanSummary}</p>
      </div>

      {/* Key Points - Bullet format for AIO extraction */}
      {finalKeyPoints.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <h4 className="text-sm font-semibold text-muted-foreground mb-3">Key Points:</h4>
          <ul className="space-y-2">
            {finalKeyPoints.map((point, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Freshness Signal */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10 text-xs text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span>Updated {dynamicDate}</span>
        <span className="mx-2">•</span>
        <span className="text-primary font-medium">Verified Information</span>
      </div>
    </motion.div>
  );
};

/**
 * AISnippetBlockCompact - Smaller version for sidebar or secondary use
 */
export const AISnippetBlockCompact = ({ quickAnswer, lastUpdated }) => {
  if (!quickAnswer) return null;

  const cleanSummary = useMemo(() => generateEliteSummary(quickAnswer), [quickAnswer]);
  const dynamicDate = useMemo(() => getCurrentMonthYear(), []);

  return (
    <div className="bg-card/50 border border-white/10 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-4 h-4 text-primary" />
        <span className="text-xs font-semibold text-primary">Quick Answer</span>
      </div>
      <p className="text-sm text-muted-foreground">{cleanSummary}</p>
      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
        <Clock className="w-3 h-3" />
        <span>Updated {dynamicDate}</span>
      </div>
    </div>
  );
};

/**
 * AISnippetBlockFeatured - Hero-style for landing pages
 */
export const AISnippetBlockFeatured = ({
  title,
  quickAnswer,
  expandedAnswer,
  keyPoints = [],
  lastUpdated,
  ctaText = 'Get Free Trial',
  ctaLink = 'https://xtreamcube.shop/freetrialiptv/',
  content
}) => {
  if (!quickAnswer) return null;

  const cleanSummary = useMemo(() => generateEliteSummary(quickAnswer), [quickAnswer]);
  const dynamicDate = useMemo(() => getCurrentMonthYear(), []);
  const finalKeyPoints = useMemo(() => {
    return keyPoints.length > 0 ? keyPoints : (content ? extractEliteKeyPoints(content, 4, 6) : []);
  }, [keyPoints, content]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden bg-gradient-to-br from-primary/20 via-background to-card/50 border border-primary/30 rounded-2xl p-8 mb-12"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-primary/20 rounded-xl">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">🔍 {title || 'Quick Answer'}</h2>
            <p className="text-sm text-muted-foreground">Updated for 2026</p>
          </div>
        </div>

        {/* Quick Answer */}
        <div className="bg-white/5 rounded-xl p-6 mb-6">
          <p className="text-lg leading-relaxed">{cleanSummary}</p>
        </div>

        {/* Expanded Answer */}
        {expandedAnswer && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-primary mb-2">In Detail:</h3>
            <p className="text-muted-foreground leading-relaxed">{generateEliteSummary(expandedAnswer)}</p>
          </div>
        )}

        {/* Key Points Grid */}
        {finalKeyPoints.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {finalKeyPoints.map((point, index) => (
              <div key={index} className="flex items-start gap-2 bg-white/5 rounded-lg p-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm">{point}</span>
              </div>
            ))}
          </div>
        )}

        {/* Footer with CTA */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Updated {dynamicDate}</span>
            <span className="mx-2">•</span>
            <span className="text-primary">✓ 2026 Verified</span>
          </div>

          {ctaLink && (
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition"
            >
              {ctaText}
              <Sparkles className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </motion.section>
  );
};

/**
 * Helper: Format quick answer from article data
 */
export const formatQuickAnswer = (article) => {
  if (article.quickAnswer) return article.quickAnswer;

  // Auto-generate from content if not provided
  const content = article.content || article.excerpt || '';
  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 20);
  return sentences.slice(0, 2).join('. ').trim() + '.';
};

/**
 * Helper: Generate key points from article
 */
export const generateKeyPoints = (article, maxPoints = 4) => {
  if (article.keyPoints) return article.keyPoints.slice(0, maxPoints);

  // Default key points based on common IPTV topics
  return [
    '20,000+ live channels available',
    '4K & HD streaming quality',
    '24/7 customer support',
    'Free trial available'
  ].slice(0, maxPoints);
};

export default AISnippetBlock;
